import { defineMessages, useIntl } from 'react-intl';
import { array, boolean, object, string } from 'yup';

import type { DosageStep } from '@utils/fhir-mappers/dosageSteps';
import { hasMedicineAmount } from '@utils/fhir-mappers/medicationIngredients';
import { dateNotBeforeSchema } from '@utils/validation';

const messages = defineMessages({
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  rangeInvalid: {
    id: 'medicationsDrilldown.rangeInvalid',
    defaultMessage:
      'The high amount must be greater than or equal to the low amount',
  },
  mustBePositive: {
    id: 'medicationsDrilldown.mustBePositive',
    defaultMessage: 'Enter a number greater than zero',
  },
  everyRequired: {
    id: 'medicationsDrilldown.everyRequired',
    defaultMessage: 'Enter the lower bound of the interval as well',
  },
  endBeforeStart: {
    id: 'GLOBAL.dateValidation.endBeforeStart',
    defaultMessage: 'End date cannot be before start date',
  },
  amountRequired: {
    id: 'medicationsDrilldown.amountRequired',
    defaultMessage: 'Enter a strength, a dose, or both',
  },
  ceilingNeedsForm: {
    id: 'medicationsDrilldown.ceilingNeedsForm',
    defaultMessage:
      'A dose limit needs a unit. Pick a form other than Unknown, or clear the limit.',
  },
});

/**
 * Validation for the medication form.
 *
 * Lifted out of the view so the rules can be tested against the real schema.
 * Several of them span fields — Form is required by what the strength, the dose
 * and the ceiling hold; "at least one" reads two arrays — and a test that
 * reimplements those in a harness proves the harness, not the rule.
 */
export function useMedicationSchema() {
  const { formatMessage } = useIntl();

  /**
   * Every numeric field is a string carrying a number, and the inputs are
   * type="number" without a native min, so a negative or zero used to validate
   * and save: doseValue '-5' wrote {value: -5}, and a zero period made a ratio a
   * division by zero.
   */
  const positiveNumber = () =>
    string().test(
      'positive-number',
      formatMessage(messages.mustBePositive),
      (value) => {
        if (!value) return true;
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0;
      }
    );

  // Looser than Schema<Partial<MedicationValues>>: the nested dosageSteps
  // object schema infers optional fields, which yup can't reconcile with the
  // required DosageStep[] shape. Validation behaviour is unaffected.
  const medValidationSchema = object().shape({
    title: string().required(formatMessage(messages.required)),
    acquisitionType: string(),
    endingDate: dateNotBeforeSchema(
      'startingDate',
      formatMessage(messages.endBeforeStart)
    ),
    dosageSteps: array()
      .of(
        object().shape({
          doseValue: positiveNumber(),
          doseValueHigh: positiveNumber().test(
            'high-gte-low',
            formatMessage(messages.rangeInvalid),
            (high, ctx) => {
              if (!high) return true;
              const low = parseFloat(
                (ctx.parent as { doseValue?: string }).doseValue ?? ''
              );
              const hi = parseFloat(high);
              return Number.isNaN(low) || Number.isNaN(hi) || hi >= low;
            }
          ),
          // Written from the Form dropdown, which carries the requirement now:
          // asking for it here would fail against a field nobody can see.
          doseUnit: string(),
          frequencyPreset: string(),
          frequencyTimes: positiveNumber(),
          frequencyEvery: positiveNumber(),
          frequencyEveryMax: positiveNumber()
            // tim-6: periodMax is meaningless without period, and the writer
            // drops it, so ask for the pair rather than silently ignoring it.
            .test(
              'interval-needs-low',
              formatMessage(messages.everyRequired),
              (high, ctx) =>
                !high ||
                Boolean(
                  (ctx.parent as { frequencyEvery?: string }).frequencyEvery
                )
            )
            .test(
              'interval-high-gte-low',
              formatMessage(messages.rangeInvalid),
              (high, ctx) => {
                if (!high) return true;
                const low = parseFloat(
                  (ctx.parent as { frequencyEvery?: string }).frequencyEvery ??
                    ''
                );
                const hi = parseFloat(high);
                return Number.isNaN(low) || Number.isNaN(hi) || hi >= low;
              }
            ),
          frequencyUnit: string(),
          frequencyCount: positiveNumber(),
          rateValue: positiveNumber(),
          rateUnit: string().when('rateValue', ([v], schema) =>
            v
              ? schema.required(formatMessage(messages.required))
              : schema.notRequired()
          ),
          ratePerValue: positiveNumber(),
          ratePerUnit: string(),
          asNeeded: boolean(),
          timing: array().of(string().required()),
          durationValue: positiveNumber(),
          durationUnit: string().when('durationValue', ([d], schema) =>
            d
              ? schema.required(formatMessage(messages.required))
              : schema.notRequired()
          ),
          // The ceiling's unit comes from Form and has no control of its own,
          // so the rule has to hang off the value, which does. Blank against a
          // ceiling means Form is Unknown, and `buildMaxDosePerPeriod` writes
          // nothing without a unit — on a full PUT that deletes a limit the
          // record already had, which is not something to do quietly.
          maxDoseValue: positiveNumber().test(
            'ceiling-needs-form',
            formatMessage(messages.ceilingNeedsForm),
            (value, ctx) =>
              !value || Boolean((ctx.parent as DosageStep).maxDoseUnit)
          ),
          maxDosePeriodValue: positiveNumber(),
          // Written from the Form dropdown, same as doseUnit.
          maxDoseUnit: string(),
          maxDosePeriodUnit: string().when('maxDoseValue', ([v], schema) =>
            v
              ? schema.required(formatMessage(messages.required))
              : schema.notRequired()
          ),
          asDirected: string(),
        })
      )
      .min(1)
      // Patient self-report requires a dose + a schedule on the first step.
      // PRN on its own counts as a schedule ("take when needed").
      .test(
        'patient-step1-required',
        formatMessage(messages.required),
        (steps, ctx) => {
          if (!(ctx.parent as { isPatient?: boolean }).isPatient) return true;
          const first = steps?.[0];
          return Boolean(
            first?.doseValue &&
            first.doseUnit &&
            (first.frequencyPreset || first.asNeeded)
          );
        }
      ),
    // The noun for the strength, the dose and the ceiling. Always required:
    // hydration seeds it from whatever unit the record already stores, so a
    // legacy record arrives with the field filled rather than blank, and there
    // is no shape where a curator is blocked by a value they never had.
    doseForm: string().required(formatMessage(messages.required)),
    ingredients: array()
      .of(
        object().shape({
          name: string(),
          strengthValue: positiveNumber(),
          strengthUnit: string().when('strengthValue', ([v], schema) =>
            v
              ? schema.required(formatMessage(messages.required))
              : schema.notRequired()
          ),
          strengthPerValue: positiveNumber(),
          // Written from the Form dropdown, same as doseUnit.
          strengthPerUnit: string(),
        })
      )
      // "How much medicine" is one requirement covering both halves, so the
      // check spans two fields and the message belongs to neither. Hung off the
      // ingredients array because that is where MedicineAmount reads it back.
      .test(
        'strength-or-dose-required',
        formatMessage(messages.amountRequired),
        (rows, ctx) => {
          const { dosageSteps, requireAmount } = ctx.parent as {
            dosageSteps?: DosageStep[];
            requireAmount?: boolean;
          };
          if (!requireAmount) return true;
          return hasMedicineAmount(rows, dosageSteps?.[0]?.doseValue);
        }
      ),
    instructions: string(),
    route: string(),
    startingDate: string(),
    selfReported: boolean().required(formatMessage(messages.required)),
    associatedResources: array().of(
      string().required(formatMessage(messages.required))
    ),
    status: string(),
    isPatient: boolean(),
    requireAmount: boolean(),
  });

  return medValidationSchema;
}
